import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import orderTicket from '../lib/orderTicket';
import './ReceiptPage.css';

export default function ReceiptPage() {
  const { orderNumber, groupId } = useParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const isGroup = !!groupId;

  useEffect(() => {
    const load = isGroup
      ? api.getOrderGroup(groupId)
      : api.getOrder(orderNumber).then((d) => [d]);

    load
      .then((data) => setOrders(Array.isArray(data) ? data : [data]))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderNumber, groupId, isGroup]);

  const accepted = orderTicket.acceptedOrders(orders);
  const liveOrders = accepted.filter((o) => o.status !== 'cancelled');
  const displayOrders = liveOrders.length > 0 ? liveOrders : accepted;

  useEffect(() => {
    if (!loading && displayOrders.length > 0) {
      const timer = setTimeout(() => window.print(), 400);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [loading, displayOrders.length]);

  if (loading) {
    return <div className="receipt-loading">Loading receipt...</div>;
  }

  if (orders.length === 0) {
    return <div className="receipt-loading">Order not found</div>;
  }

  if (displayOrders.length === 0) {
    return <div className="receipt-loading">No receipt is available. This payment was not completed.</div>;
  }

  const allItems = [];
  let grandTotal = 0;
  const allNotes = [];
  const orderNumbers = [];
  let customerName = '';
  let createdAt = '';
  let paymentMethod = '';

  for (const order of displayOrders) {
    orderNumbers.push(order.order_number);
    if (order.customer_name && order.customer_name !== 'Guest') {
      customerName = order.customer_name;
    }
    if (!createdAt && order.created_at) createdAt = order.created_at;
    if (order.payment_method) paymentMethod = order.payment_method;
    grandTotal += order.total;
    if (order.notes) allNotes.push(order.notes);
    for (const item of order.items) {
      const opts = item.options
        ? typeof item.options === 'string' ? JSON.parse(item.options) : item.options
        : null;
      const optsKey = opts ? JSON.stringify(opts) : '';
      const key = `${item.item_name}::${optsKey}`;
      const existing = allItems.find((m) => m.key === key);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        // Render the full key/value pairs so the printed receipt shows e.g.
        // "Type: Grilled" rather than the bare value, which is ambiguous on a
        // multi-modifier item (Size + Topping looked like a comma-separated
        // mystery list with no field labels).
        const optsLabel = opts
          ? Object.entries(opts)
              .filter(([, v]) => v)
              .map(([k, v]) => `${k}: ${v}`)
              .join(' \u00B7 ')
          : '';
        allItems.push({
          key,
          name: item.item_name,
          price: item.unit_price,
          quantity: item.quantity,
          options: optsLabel,
        });
      }
    }
  }

  const payLabel = paymentMethod === 'card_paid' ? 'Card' : paymentMethod === 'card' ? 'Card' : 'Counter';
  const dateStr = createdAt
    ? new Date(createdAt).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
    : new Date().toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div className="receipt-page">
      <div className="receipt">
        <div className="receipt-header">
          <h1>St Barbara & St Noufer</h1>
          <p className="receipt-subtitle">Order Receipt</p>
        </div>

        <div className="receipt-meta">
          <div className="receipt-row">
            <span>Order #</span>
            <span>{orderNumbers.join(', ')}</span>
          </div>
          <div className="receipt-row">
            <span>Date</span>
            <span>{dateStr}</span>
          </div>
          {customerName && (
            <div className="receipt-row">
              <span>Customer</span>
              <span>{customerName}</span>
            </div>
          )}
          <div className="receipt-row">
            <span>Payment</span>
            <span>{payLabel}</span>
          </div>
        </div>

        <div className="receipt-divider" />

        <div className="receipt-items">
          {allItems.map((item) => (
            <div key={item.key} className="receipt-item">
              <div className="receipt-item-desc">
                <span>{item.quantity}x {item.name}</span>
                {item.options && <span className="receipt-item-opts">{item.options}</span>}
              </div>
              <span className="receipt-item-price">${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>

        <div className="receipt-divider" />

        <div className="receipt-total">
          <span>Total</span>
          <span>${grandTotal.toFixed(2)}</span>
        </div>

        {allNotes.length > 0 && (
          <div className="receipt-notes">
            <strong>Notes:</strong> {allNotes.join(' | ')}
          </div>
        )}

        <div className="receipt-footer">
          <p>Thank you for your order!</p>
        </div>
      </div>

      <div className="receipt-actions no-print">
        <button className="btn btn-primary" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
        <button className="btn btn-outline" onClick={() => window.close()}>
          Close
        </button>
      </div>
    </div>
  );
}
